/**
 * Pure meal-plan sale rules (no Firebase). One payment covers one week.
 */

import type { MealPlanSubscription, MealPlanType, OrderItem } from '../types';
import { weekBoundsMs } from './mealPlanWeek';

export function mealPlanSalePriceDollars(priceCents: number): number {
  return Math.round(Math.max(0, Number(priceCents) || 0)) / 100;
}

export function mealPlanSaleLine(plan: MealPlanType, weekId: string, lineId = Date.now()): OrderItem {
  return {
    id: lineId,
    name: `Meal plan · ${plan.name}`,
    price: mealPlanSalePriceDollars(plan.priceCents),
    categoryId: 'meal-plan',
    imageUrl: '',
    quantity: 1,
    notes: `Week of ${weekId}`,
    lineId: `mpsale-${plan.id}-${weekId}`,
    status: 'completed',
    quantityCompleted: 1,
    isMealPlanSale: true,
  };
}

/** True when an active subscription already covers this Monday week id. Missing window does not cover a future week. */
export function subscriptionCoversWeek(
  sub: Pick<MealPlanSubscription, 'status' | 'activeFrom' | 'activeTo' | 'coveredWeekId'>,
  weekId: string,
): boolean {
  if (sub.status !== 'active') return false;
  if (sub.coveredWeekId && sub.coveredWeekId !== weekId) return false;
  const { weekStartMs, weekEndMs } = weekBoundsMs(weekId);
  if (sub.activeFrom > weekEndMs) return false;
  if (sub.activeTo != null && sub.activeTo < weekStartMs) return false;
  if (sub.activeTo == null && !sub.coveredWeekId) return false;
  return true;
}

export function customerHasPlanForWeek(
  subs: MealPlanSubscription[],
  customerId: string,
  weekId: string,
  planTypeId: string,
): boolean {
  const id = String(customerId);
  return subs.some(
    (s) => String(s.customerId) === id && s.planTypeId === planTypeId && subscriptionCoversWeek(s, weekId),
  );
}

export function customerAlreadyOnWeek(
  subs: MealPlanSubscription[],
  customerId: string,
  weekId: string,
): boolean {
  const id = String(customerId);
  return subs.some((s) => String(s.customerId) === id && subscriptionCoversWeek(s, weekId));
}

export function posMealPlanSubscriptionId(orderId: string): string {
  return `mps-pos-${orderId}`;
}
